import { Badge } from '@mantine/core';
import { IconBellRinging, IconCalendarMonth, IconPencil, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getCategoryColor } from 'src/Components/PracticalInformation/practicalInformationTypeColor.helper';
import ICONS from 'src/Components/Shared/IconSelect/icons';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { CreatePracticalInformationFieldDtoFieldTypeEnum } from 'src/Services/API/generated';
import { humanizeRRule, isRecurrenceDto } from 'src/Services/Shared/humanizeRRule';

import styles from './PracticalInformationCard.module.scss';
import { Props } from './PracticalInformationCard.types';

const PracticalInformationCard = ({
  title,
  fields,
  createdAt,
  type,
  icon,
  subtype,
  onDelete,
  readonly,
  id,
  onClick,
  reminderTimeOfDay,
  reminderFieldKey,
}: Props) => {
  const Icon = icon && ICONS[icon as keyof typeof ICONS];
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const assetAboveQuotas = useIsAssetAboveQuotas();
  const categoryColor = getCategoryColor(type);

  return (
    <div
      className={styles.container}
      onClick={onClick}
      style={{ borderLeft: `4px solid ${categoryColor.hex}` }}
    >
      <div className={styles.topLine}>
        <Badge
          leftSection={Icon && <Icon height={16} className={styles.icon} />}
          color={categoryColor.mantineColor}
          variant="light"
        >
          {subtype ? (
            <span className={styles.type}>{t(`practicalInformation.subtypes.${subtype}`)}</span>
          ) : (
            <span className={styles.type}>{t(`practicalInformation.types.${type}`)}</span>
          )}
        </Badge>
        {!readonly && (
          <RowActions
            actions={[
              {
                key: 'edit',
                label: t('practicalInformation.menu.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                disabled: assetAboveQuotas,
                onClick: () => navigate(`${id}/edit`),
              },
              {
                key: 'delete',
                label: t('practicalInformation.menu.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                onClick: onDelete,
              },
            ]}
          />
        )}
      </div>
      <div>
        <div className={styles.fieldKey}>{title}</div>
        {fields
          .slice(0, 4)
          .filter(field => Boolean(field.value))
          .map((field, index) => {
            return (
              <div className={styles.field} key={[field.key, index].join('-')}>
                <div className={styles.fieldKey}>
                  {`${t(`practicalInformation.placeholders.${field.key}`)} : `}
                </div>
                <div className={styles.fieldValue}>
                  {field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Date
                    ? dayjs(field.value.toString()).locale(i18n.language).format('LL')
                    : field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule
                      ? isRecurrenceDto(field.value)
                        ? humanizeRRule(field.value)
                        : 'not a recurrence'
                      : field.value?.toString()}
                </div>
              </div>
            );
          })}
      </div>
      <div className={styles.bottomLine}>
        <IconCalendarMonth height={12} className={styles.calendarIcon} />
        <div className={styles.date}>{createdAt.locale(i18n.language).format('L')}</div>
        {reminderFieldKey && (
          <div className={styles.reminder}>
            <IconBellRinging height={12} className={styles.reminderIcon} />
            <div className={styles.reminderTime}>
              {reminderTimeOfDay
                ? t('practicalInformation.reminder.cardLabel', 'Rappel à {{ time }}', {
                    time: reminderTimeOfDay,
                  })
                : t('practicalInformation.reminder.cardLabelNoTime', 'Rappel actif')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticalInformationCard;
